import {Map as GoogleMap, useMap} from "@vis.gl/react-google-maps";
import type {Location} from "@/api/apiClient";
import {useOrientation} from "@/hooks/useOrientation";
import {ContextMenu} from "./components/ContextMenu";
import {DriverMarkers} from "./components/DriverMarkers";
import {LocationMarker} from "./components/LocationMarker";
import {MapAttributionStyle} from "./components/MapAttributionStyle";
import {MapControls} from "./components/MapControls";
import {MapErrorBoundary} from "./components/MapErrorBoundary";
import {Polyline} from "./components/Polyline";
import {useContextMenu} from "./hooks/useContextMenu";
import {useMapPosition} from "./hooks/useMapPosition";
import {useMapZoom} from "./hooks/useMapZoom";
import {useMapZoomControls} from "./hooks/useMapZoomControls";
import {useMarkersOverlap} from "./hooks/useMarkersOverlap";
import {getMapConfig} from "./utils/config";
import {MAP_CONFIG, POLYLINE_STYLE} from "./utils/constants";
import type {DriverData, LatLng, SheetOffset} from "./utils/types";

export type MapProps = {
    onSetDropoff: (location: LatLng) => void;
    onSetPickup: (location: LatLng) => void;
    defaultCenter: LatLng;
    rideShape?: string;
    sheetOffset: SheetOffset;
    isConfirmation?: boolean;
    isFixedPickup?: boolean;
    isFixedDestination?: boolean;
    pickup?: Location;
    dropoff?: Location;
    drivers?: DriverData[];
};

//TODO
// - make zoom level right for map with pickup only

export function Map(props: MapProps) {
    return (
        <MapErrorBoundary>
            <MapContent {...props} />
        </MapErrorBoundary>
    );
}

function MapContent({
    onSetDropoff,
    onSetPickup,
    defaultCenter,
    rideShape,
    sheetOffset,
    isConfirmation,
    isFixedPickup,
    isFixedDestination,
    pickup,
    dropoff,
    drivers,
}: MapProps) {
    const map = useMap();
    const {isPortrait} = useOrientation();
    const zoom = useMapZoom(map);

    const {showResetButton, resetMapPosition} = useMapPosition({
        map,
        pickup,
        dropoff,
        rideShape,
        defaultCenter,
        sheetOffset,
        isPortrait,
    });

    const {zoomIn, zoomOut} = useMapZoomControls(map);
    const markersOverlap = useMarkersOverlap(map, pickup, dropoff, zoom);

    const mapConfig = getMapConfig(isConfirmation);

    const {menuPosition, isMenuVisible, contextMenuCoordinates, handleMapContextMenu, hideMenu} =
        useContextMenu();

    const handleSetDropoff = () => {
        onSetDropoff(contextMenuCoordinates);
        hideMenu();
    };

    const handleSetPickup = () => {
        onSetPickup(contextMenuCoordinates);
        hideMenu();
    };

    return (
        <>
            {isPortrait && <MapAttributionStyle offset={sheetOffset} />}
            <GoogleMap
                mapId={MAP_CONFIG.MAP_ID}
                disableDefaultUI={true}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                clickableIcons={false}
                defaultCenter={defaultCenter}
                defaultZoom={MAP_CONFIG.DEFAULT_ZOOM}
                onClick={mapConfig.isInteractive ? handleMapContextMenu : undefined}
                onContextmenu={mapConfig.isInteractive ? handleMapContextMenu : undefined}
                onDragend={hideMenu}
                onCenterChanged={hideMenu}
                onDblclick={hideMenu}
            >
                <ContextMenu
                    onSetDropoff={handleSetDropoff}
                    onSetPickup={handleSetPickup}
                    isFixedPickup={isFixedPickup}
                    isFixedDestination={isFixedDestination}
                    position={menuPosition}
                    visible={isMenuVisible}
                />

                {rideShape && <Polyline encodedPaths={[rideShape]} {...POLYLINE_STYLE} />}

                {drivers && mapConfig.showDrivers && (
                    <DriverMarkers drivers={drivers} zoom={zoom} />
                )}

                {pickup && (
                    <LocationMarker
                        location={pickup}
                        type="pickup"
                        isInteractive={mapConfig.isInteractive && !isConfirmation && !isFixedPickup}
                    />
                )}

                {dropoff && (
                    <LocationMarker
                        location={dropoff}
                        type="dropoff"
                        isInteractive={
                            mapConfig.isInteractive && !isConfirmation && !isFixedDestination
                        }
                        isFlipped={markersOverlap}
                    />
                )}
            </GoogleMap>

            <MapControls
                sheetOffset={sheetOffset}
                isPortrait={isPortrait}
                showResetButton={showResetButton}
                onResetPosition={resetMapPosition}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
            />
        </>
    );
}
